import "./header.scss";
import {
  createStyles,
  Header,
  Group,
  Button,
  Avatar,
  Menu,
  Text,
  ScrollArea,
  UnstyledButton,
  Divider,
} from "@mantine/core";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { logout } from "../../actions/auth";
import menuData from "../Sidebar/links";
import { getApiEnvironmentLabel } from "../../services/apiConfig";
import AuthService from "../../services/api";
import { PATH } from "../../constants/paths";
import partnerBrandLogo from "../../assets/img/Images/affordamic_partner_logo.jpg";
import {
  IconAlertCircle,
  IconBell,
  IconBook,
  IconBooks,
  IconBulb,
  IconChartBar,
  IconCheckbox,
  IconChevronDown,
  IconClipboardCheck,
  IconClipboardList,
  IconCircle,
  IconCreditCard,
  IconDiscount2,
  IconFileDescription,
  IconFolders,
  IconGauge,
  IconHelp,
  IconHome,
  IconLayoutDashboard,
  IconLayoutList,
  IconListDetails,
  IconTicket,
  IconMessageCircle,
  IconNews,
  IconNotebook,
  IconNotes,
  IconAdjustments,
  IconSettings,
  IconShieldLock,
  IconKey,
  IconTrophy,
  IconUpload,
  IconUsers,
  IconBuildingCommunity,
  IconLogout,
} from "@tabler/icons";

const useStyles = createStyles((theme) => ({
  header: {
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
  },

  topRow: {
    height: 54,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
    zIndex: 400,
  },

  navRow: {
    height: 52,
    display: "flex",
    alignItems: "center",
    borderTop: `1px solid ${theme.colors.gray[2]}`,
    overflow: "visible",
    background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
    position: "relative",
    zIndex: 100,
  },

  topNavButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12.5,
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: "0.15px",
    whiteSpace: "nowrap",
    color: theme.colors.dark[6],
    border: `1px solid transparent`,
    transition: "all 160ms ease",
    "&:hover": {
      backgroundColor: theme.colors.blue[0],
      color: theme.colors.blue[7],
      borderColor: theme.colors.blue[2],
      transform: "translateY(-1px)",
    },
  },

  activeTopNav: {
    backgroundColor: "#e7f5ff",
    color: theme.colors.blue[7],
    borderColor: theme.colors.blue[2],
    boxShadow: "0 1px 2px rgba(34, 139, 230, 0.16)",
  },

  appTitle: {
    fontWeight: 700,
    fontSize: 16,
    color: theme.colors.dark[7],
  },

  partnerBrand: {
    height: 40,
    width: "auto",
    maxWidth: 240,
    display: "block",
    objectFit: "contain",
    cursor: "pointer",
  },

  partnerAvatar: {
    backgroundColor: "#fff",
    border: "1px solid #e2e8f0",
    "& img": {
      objectFit: "contain",
      padding: 4,
    },
  },

  compactMenuItem: {
    fontSize: 12.5,
    fontWeight: 600,
    borderRadius: 6,
  },

  nestedMenuItem: {
    paddingLeft: 20,
    fontSize: 12,
    color: theme.colors.gray[7],
    borderRadius: 6,
  },

  rootMenuItem: {
    fontSize: 12.8,
    fontWeight: 600,
    color: theme.colors.dark[7],
    borderRadius: 6,
  },

  nestedMenuLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: theme.colors.gray[6],
    padding: "6px 10px 4px",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
  },

  menuPanel: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 4,
    width: "100%",
    padding: "4px 2px",
  },

  menuColumn: {
    minWidth: 0,
    padding: "2px 8px",
    "& + &": {
      borderLeft: "1px solid #eef2f7",
    },
  },

  menuColumnLabel: {
    margin: "4px 10px 6px",
    color: "#94a3b8",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },

  menuLink: {
    width: "100%",
    marginBottom: 2,
    borderRadius: 8,
    color: "#334155",
    fontSize: 13,
    fontWeight: 600,
    "& svg": {
      color: "#64748b",
    },
    "&:hover": {
      background: "#f1f5f9",
      color: "#0f172a",
    },
  },

  menuLinkActive: {
    background: "#e7f5ff",
    color: "#1c7ed6",
    "& svg": {
      color: "#1c7ed6",
    },
    "&:hover": {
      background: "#d0ebff",
      color: "#1c7ed6",
    },
  },

  topNavIcon: {
    width: 14,
    height: 14,
    objectFit: "contain",
    flexShrink: 0,
  },

  topNavText: {
    whiteSpace: "nowrap",
    lineHeight: 1,
  },

  hideOnSmall: {
    [theme.fn.smallerThan("sm")]: {
      display: "none",
    },
  },
}));

export default function HeaderSearch() {
  const [isClicked, setIsClicked] = useState(false);
  const [partnerLogo, setPartnerLogo] = useState("");
  const [partnerName, setPartnerName] = useState("");
  const dropdownRef = useRef();

  const dispatch = useDispatch();

  const handleClick = () => {
    setIsClicked(!isClicked);
  };
  const { classes } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const role = localStorage.getItem("user_role") || "admin";
  const userName = localStorage.getItem("username") || "username";
  const userEmail = localStorage.getItem("user_email") || userName;
  const envLabel = getApiEnvironmentLabel();
  const linksData = useMemo(() => menuData(role) || [], [role]);
  const partnerAvatarSrc = role === "partner" ? partnerLogo || undefined : undefined;
  const partnerInitial = (partnerName || userEmail || "P").slice(0, 1).toUpperCase();

  const isActiveLink = (item) => {
    if (!item || !item.link || item.link === "#") return false;
    if (location.pathname === item.link) return true;
    return location.pathname.startsWith(`${item.link}/`);
  };

  const hasActiveChild = (children = []) => {
    return children.some((child) => {
      if (isActiveLink(child)) return true;
      if (Array.isArray(child.links) && child.links.length > 0) {
        return hasActiveChild(child.links);
      }
      return false;
    });
  };

  const flattenChildLinks = (children = []) => {
    const items = [];
    children.forEach((child) => {
      if (child.links && child.links.length) {
        items.push({ type: "label", label: child.label });
        child.links.forEach((nested) => items.push({ type: "item", depth: 1, ...nested }));
      } else {
        items.push({ type: "item", depth: 0, ...child });
      }
    });
    return items;
  };

  const groupedChildLinks = (children = []) => {
    const groups = [];
    const direct = [];
    children.forEach((child) => {
      if (Array.isArray(child.links) && child.links.length) groups.push(child);
      else direct.push(child);
    });
    const total =
      groups.reduce((count, group) => count + group.links.length, 0) + direct.length;
    return { groups, direct, useColumns: groups.length > 0 && total > 8 };
  };

  const getMenuItemIcon = (label) => {
    const iconMap = {
      Series: IconBooks,
      Tests: IconClipboardCheck,
      "Practice papers": IconNotes,
      "Exam Repository": IconFolders,
      "Mock Test Creator": IconBulb,
      "Listing and Content": IconLayoutList,
      "Control Board & Exams": IconAdjustments,
      Questions: IconHelp,
      Stats: IconChartBar,
      "Review Workspace": IconCheckbox,
      "Bulk Add": IconUpload,
      Stories: IconNews,
      "Learning Library": IconNotebook,
      "Library Subscription": IconTicket,
      "Static Contents": IconFileDescription,
    };
    return iconMap[label] || null;
  };

  const getTopNavIcon = (label) => {
    const iconMap = {
      Dashboard: IconLayoutDashboard,
      Education: IconBook,
      Competitions: IconTrophy,
      "Chat Forums": IconMessageCircle,
      "Promotions & Offers": IconDiscount2,
      Partners: IconBuildingCommunity,
      Members: IconUsers,
      Credits: IconCreditCard,
      "Push Notifications": IconBell,
      Billings: IconCreditCard,
      Accounts: IconUsers,
      Settings: IconSettings,
      Lists: IconListDetails,
      Configuration: IconSettings,
      Notifications: IconBell,
      "FAQ's": IconAlertCircle,
      "Reporting & Issues": IconClipboardList,
    };
    return iconMap[label] || IconCircle;
  };

  useEffect(() => {
    if (role !== "partner") return undefined;
    AuthService.getPartnerPortal()
      .then((response) => {
        const partner = response?.data?.data?.partner;
        setPartnerLogo(partner?.logo || "");
        setPartnerName(partner?.name || "");
      })
      .catch(() => {});
    return undefined;
  }, [role]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsClicked(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <Header
      height={106}
      className={classes.header}
      style={{ overflow: "visible", zIndex: 300 }}
    >
      <div className={classes.topRow}>
        <Group spacing={8}>
          {role === "partner" ? (
            <img
              src={partnerBrandLogo}
              alt="Affordamic Partner"
              className={classes.partnerBrand}
              onClick={() => navigate("/partner/dashboard")}
            />
          ) : (
            <>
              <IconHome size={18} color="#228BE6" />
              <Text className={classes.appTitle}>Affordamic Admin</Text>
            </>
          )}
        </Group>

        <Group>
          {role !== "partner" ? (
            <Group ml={50} spacing={5} className={classes.hideOnSmall}>
              <Button
                variant="light"
                onClick={() => {
                  if (localStorage.getItem("user_role") === "admin") {
                    navigate("/admin/dashboard");
                  } else if (
                    localStorage.getItem("user_role") === "content_feeder" ||
                    localStorage.getItem("user_role") === "content_validator"
                  ) {
                    navigate("/cm/dashboard");
                  }
                }}
              >
                Home
              </Button>
              <Button
                variant="light"
                color={"red"}
                onClick={() => {
                  localStorage.clear();
                  dispatch(logout());
                  navigate("/login");
                }}
              >
                Logout
              </Button>
            </Group>
          ) : null}
          <div className="header_profile_container" ref={dropdownRef}>
            <Avatar
              src={partnerAvatarSrc}
              alt={partnerName || "Profile"}
              color="cyan"
              radius="xl"
              className={role === "partner" ? classes.partnerAvatar : undefined}
              onClick={handleClick}
              style={{ cursor: "pointer" }}
            >
              {role === "partner" ? partnerInitial : null}
            </Avatar>
            {isClicked && (
              <div className="header_onclick_container">
                <div className="header_account">
                  <Avatar
                    src={partnerAvatarSrc}
                    alt={partnerName || "Profile"}
                    color="cyan"
                    radius="xl"
                    size={40}
                    className={role === "partner" ? classes.partnerAvatar : undefined}
                  >
                    {role === "partner"
                      ? partnerInitial
                      : String(userEmail || "A").slice(0, 1).toUpperCase()}
                  </Avatar>
                  <div className="header_account_meta">
                    <p className="header_username">{userEmail}</p>
                    <p className="header_account_hint">Signed in</p>
                  </div>
                </div>
                <div className="header_meta_list">
                  <div className="header_meta_row">
                    <span>Role</span>
                    <strong className="header_pill header_pill_role">
                      {role.replace(/_/g, " ")}
                    </strong>
                  </div>
                  <div className="header_meta_row">
                    <span>Environment</span>
                    <strong className={`header_pill header_pill_env header_pill_env_${envLabel.toLowerCase()}`}>
                      {envLabel}
                    </strong>
                  </div>
                </div>
                {role !== "partner" ? (
                <div className="header_profile_menu">
                  <p className="header_profile_menu_label">Security</p>
                  <button
                    type="button"
                    className="header_profile_menu_item"
                    onClick={() => {
                      setIsClicked(false);
                      navigate("/settings/change-password");
                    }}
                  >
                    <IconKey size={16} />
                    Change password
                  </button>
                  <button
                    type="button"
                    className="header_profile_menu_item"
                    onClick={() => {
                      setIsClicked(false);
                      navigate("/settings/two-factor");
                    }}
                  >
                    <IconShieldLock size={16} />
                    Two-factor authentication
                  </button>
                </div>
                ) : null}
                <button
                  className="header_logout_btn"
                  onClick={() => {
                    localStorage.clear();
                    dispatch(logout());
                    navigate(role === "partner" ? PATH.PARTNER_LOGIN : "/login");
                  }}
                >
                  <IconLogout size={16} />
                  Logout
                </button>
              </div>
            )}
          </div>
        </Group>
      </div>
      <div className={classes.navRow}>
        <ScrollArea style={{ width: "100%" }} type="never">
          <Group spacing={6} noWrap>
            {linksData.map((item) => {
              const hasChildren = Array.isArray(item.links) && item.links.length > 0;
              if (!hasChildren) {
                return (
                  <UnstyledButton
                    key={item.label}
                    className={`${classes.topNavButton} ${
                      isActiveLink(item) ? classes.activeTopNav : ""
                    }`}
                    onClick={() => navigate(item.link)}
                  >
                    {React.createElement(getTopNavIcon(item.label), {
                      size: 14,
                      stroke: 1.8,
                    })}
                    <span className={classes.topNavText}>{item.label}</span>
                  </UnstyledButton>
                );
              }
              const childItems = flattenChildLinks(item.links);
              const grouped = groupedChildLinks(item.links);
              return (
                <Menu
                  key={item.label}
                  shadow="md"
                  width={grouped.useColumns ? 704 : 250}
                  position="bottom-start"
                  withinPortal
                  zIndex={400}
                >
                  <Menu.Target>
                    <UnstyledButton
                      className={`${classes.topNavButton} ${
                        hasActiveChild(item.links) ? classes.activeTopNav : ""
                      }`}
                    >
                      {React.createElement(getTopNavIcon(item.label), {
                        size: 14,
                        stroke: 1.8,
                      })}
                      <span className={classes.topNavText}>{item.label}</span>
                      <IconChevronDown size={14} />
                    </UnstyledButton>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {grouped.useColumns ? (
                      <div className={classes.menuPanel}>
                        {grouped.groups.map((group) => (
                          <div className={classes.menuColumn} key={group.label}>
                            <div className={classes.menuColumnLabel}>{group.label}</div>
                            {group.links.map((child) => {
                              const ItemIcon = getMenuItemIcon(child.label);
                              return (
                                <Menu.Item
                                  key={child.label}
                                  icon={
                                    ItemIcon ? (
                                      <ItemIcon size={16} stroke={1.7} />
                                    ) : null
                                  }
                                  className={`${classes.menuLink} ${
                                    isActiveLink(child) ? classes.menuLinkActive : ""
                                  }`}
                                  onClick={() => child.link !== "#" && navigate(child.link)}
                                >
                                  {child.label}
                                </Menu.Item>
                              );
                            })}
                          </div>
                        ))}
                        {grouped.direct.length ? (
                          <div className={classes.menuColumn}>
                            <div className={classes.menuColumnLabel}>More</div>
                            {grouped.direct.map((child) => {
                              const ItemIcon = getMenuItemIcon(child.label);
                              return (
                                <Menu.Item
                                  key={child.label}
                                  icon={
                                    ItemIcon ? (
                                      <ItemIcon size={16} stroke={1.7} />
                                    ) : null
                                  }
                                  className={`${classes.menuLink} ${
                                    isActiveLink(child) ? classes.menuLinkActive : ""
                                  }`}
                                  onClick={() => child.link !== "#" && navigate(child.link)}
                                >
                                  {child.label}
                                </Menu.Item>
                              );
                            })}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    {!grouped.useColumns && childItems.map((child, index) => {
                      if (child.type === "label") {
                        return (
                          <React.Fragment key={`${item.label}-label-${index}`}>
                            <Text className={classes.nestedMenuLabel}>{child.label}</Text>
                            <Divider />
                          </React.Fragment>
                        );
                      }
                      return (
                        <Menu.Item
                          key={`${item.label}-${child.label}-${index}`}
                          className={
                            child.depth === 0
                              ? classes.rootMenuItem
                              : child.link === "#"
                              ? classes.compactMenuItem
                              : classes.nestedMenuItem
                          }
                          onClick={() => child.link !== "#" && navigate(child.link)}
                          disabled={child.link === "#"}
                        >
                          {child.label}
                        </Menu.Item>
                      );
                    })}
                  </Menu.Dropdown>
                </Menu>
              );
            })}
          </Group>
        </ScrollArea>
      </div>
    </Header>
  );
}
